/**
 * Cart Announcer - ARIA Live Region for Cart Updates
 * 
 * Provides screen reader announcements for cart actions without intrusive modals.
 * Implements WCAG 2.1 Success Criterion 4.1.3 (Status Messages)
 * 
 * Usage: Place once in app root (already included in ShopifyCartProvider)
 */

import { useEffect, useRef } from 'react';

export interface CartAnnouncerProps {
  message: string;
  clearMessage: () => void;
}

export const CartAnnouncer = ({ message, clearMessage }: CartAnnouncerProps) => {
  const timeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (message) {
      // Clear message after it's been announced (3 seconds)
      timeoutRef.current = setTimeout(() => {
        clearMessage();
      }, 3000);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [message, clearMessage]);

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className="sr-only"
    >
      {message}
    </div>
  );
};

/**
 * Screen-reader only utility class
 * Hides content visually but keeps it accessible to assistive technology
 */
export const srOnlyStyles = {
  position: 'absolute' as const,
  width: '1px',
  height: '1px',
  padding: '0',
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap' as const,
  borderWidth: '0',
};
